import { Reveal } from "@/lib/reveal";
import ServiceCard from "@/components/ServiceCard";
import { SERVICES } from "@/data/services";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, HeartHandshake, Info } from "lucide-react";
import AmbientVideo from "@/components/AmbientVideo";
import { AMBIENT_VIDEOS, AMBIENT_POSTERS } from "@/data/media";
import { usePageMeta } from "@/lib/usePageMeta";

const INCLUDES = [
  "Judgment-free conversation",
  "Real-life support during errands, outings, and life transitions",
  "A calm, grounded presence",
];

const NOT_INCLUDES = [
  "Therapy or counseling",
  "Medical or clinical advice",
  "Crisis or emergency services",
];

export default function ServicesPage() {
  usePageMeta({
    title: "Support Sessions & What to Expect — Calm & Connected",
    description:
      "Choose 30-, 60-, or 120-minute one-on-one peer support sessions. Conversation, walking sessions, coffee chats, and supportive companionship during errands, outings, and life transitions.",
  });
  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="pt-36 md:pt-44 pb-12 md:pb-20">
        <div className="cc-container max-w-3xl">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              Support Options
            </p>
            <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.05]">
              Choose the kind of company that feels right for you.
            </h1>
            <p className="mt-7 text-lg text-[var(--cc-text-soft)] leading-relaxed">
              Not sure which one to choose? Start with whatever feels right —
              we'll figure it out together.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Service Cards */}
      <section className="pb-12">
        <div className="cc-container">
          <Reveal>
            <AmbientVideo
              src={AMBIENT_VIDEOS.landscape}
              poster={AMBIENT_POSTERS.landscape}
              overlay="medium"
              className="w-full h-[220px] md:h-[300px] cc-card mb-12 md:mb-16"
              testId="services-ambient-video"
            >
              <div className="h-full flex items-end">
                <div
                  className="ml-6 mb-6 md:ml-10 md:mb-8 max-w-md px-6 py-4 md:px-7 md:py-5 rounded-3xl backdrop-blur-md border border-white/40"
                  style={{ background: "rgba(253, 251, 247, 0.88)" }}
                >
                  <p className="font-serif italic text-xl md:text-2xl leading-snug text-[var(--cc-text)]">
                    A space to slow down — and be heard.
                  </p>
                </div>
              </div>
            </AmbientVideo>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 100}>
                <ServiceCard service={s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section
        data-testid="services-what-to-expect"
        className="cc-section relative overflow-hidden"
      >
        <span aria-hidden className="cc-orb cc-orb-1" style={{ opacity: 0.18 }} />
        <div className="cc-container relative">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                What to expect
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                One-on-one. <em className="italic">Centered around you.</em>
              </h2>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-12 md:mt-14 max-w-3xl mx-auto cc-card p-8 md:p-12 bg-white/80 backdrop-blur-sm">
              <div className="flex justify-center">
                <span className="w-14 h-14 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center">
                  <HeartHandshake size={20} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                </span>
              </div>

              <div className="mt-7 space-y-5 text-center md:text-left text-[var(--cc-text)] leading-relaxed text-lg">
                <p>
                  All sessions are one-on-one and centered around support,
                  connection, and being heard.
                </p>
                <p>
                  Some sessions may take place by phone or video, while others
                  may be offered in person — such as coffee conversations,
                  walking sessions, supportive companionship during errands,
                  outings, or life transitions.
                </p>
                <p>
                  The goal is to provide a calm, judgment-free space where
                  people can talk, process, feel supported, and reconnect with
                  themselves.
                </p>
                <p className="text-[var(--cc-text-soft)]">
                  Not sure which option is the best fit?{" "}
                  <Link
                    to="/contact"
                    data-testid="expect-reach-out"
                    className="cc-link"
                  >
                    Reach out
                  </Link>{" "}
                  and we&apos;ll figure it out together.
                </p>
              </div>

              <div
                data-testid="expect-disclaimer"
                className="mt-8 pt-6 border-t border-[var(--cc-line)] flex items-start gap-3 text-sm text-[var(--cc-text-soft)] leading-relaxed"
              >
                <Info size={15} strokeWidth={1.6} className="mt-0.5 text-[var(--cc-primary)] shrink-0" />
                <p>
                  <span className="text-[var(--cc-text)] font-medium">
                    Please note:
                  </span>{" "}
                  Calm &amp; Connected provides peer support and companionship
                  based on lived experience. It is not therapy, counseling,
                  crisis intervention, or medical advice.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What this is / isn't */}
      <section className="cc-section bg-[var(--cc-surface)]">
        <div className="cc-container grid md:grid-cols-2 gap-8">
          <Reveal>
            <div className="cc-card p-8 md:p-10 h-full">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                What's included
              </p>
              <h3 className="mt-3 font-serif text-3xl">Every session brings</h3>
              <ul className="mt-6 space-y-3">
                {INCLUDES.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[var(--cc-text)]">
                    <Check size={18} strokeWidth={1.7} className="mt-1 text-[var(--cc-primary)]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="cc-card p-8 md:p-10 h-full">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-secondary)]">
                What this isn't
              </p>
              <h3 className="mt-3 font-serif text-3xl">Honest boundaries</h3>
              <ul className="mt-6 space-y-3">
                {NOT_INCLUDES.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[var(--cc-text-soft)]">
                    <X size={18} strokeWidth={1.7} className="mt-1 text-[var(--cc-text-soft)]/70" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-[var(--cc-text-soft)] leading-relaxed">
                If you are in crisis, please call or text 988 (US) or your local
                emergency line.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="cc-section">
        <div className="cc-container text-center max-w-2xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Still deciding? Let's talk first.
            </h2>
            <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
              Send a short note — I'll meet you where you are and help you pick
              what feels best.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/booking" data-testid="services-cta-primary" className="cc-btn-primary">
                Schedule a Conversation <ArrowRight size={16} strokeWidth={1.7} />
              </Link>
              <Link to="/contact" data-testid="services-cta-secondary" className="cc-btn-secondary">
                Send a Note
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
